import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import {
  CitationLocatorKind,
  CitationSourceKind,
  CitationTextSource,
  knownMember,
} from "ai-assistant/citations/citation-source-kind";
import { z } from "zod";

/** A Guid that carries no identity is absent, not empty. */
const emptyAsUndefined = (guid: Guid | null | undefined): Guid | undefined =>
  guid && !guid.isEmpty ? guid : undefined;

export type AssistantCitationJSON = z.infer<typeof AssistantCitationSchema.AssistantCitationJSONZ>;
export type AssistantCitationParams = z.infer<typeof AssistantCitationSchema.AssistantCitationZ>;

/** Validated citation JSON as params. The codec's decode, and `decodeParsed`. */
function decodeCitation(data: AssistantCitationJSON): AssistantCitationParams {
  return {
    ordinal: data.ordinal,
    // Decoded with allowEmpty because the API sends the nil GUID rather than omitting
    // the field: CitationResponse.FileId and NodeId are non-nullable Guids, and
    // AuthorizedRagRetrievalService falls back to Guid.Empty for any identifier it
    // cannot parse. Without this, one such citation threw here and took the rendering
    // of the entire answer with it.
    fileId: Guid.fromJSON(data.fileId, { allowEmpty: true }) ?? Guid.EMPTY,
    fileName: data.fileName,
    downloadDocumentId: emptyAsUndefined(Guid.fromJSON(data.downloadDocumentId, { allowEmpty: true })),
    sourcePath: data.sourcePath ?? undefined,
    page: data.page ?? undefined,
    pageLabel: data.pageLabel ?? undefined,
    sourceKind: knownMember(CitationSourceKind, data.sourceKind),
    // A locator without a kind says nothing about where, so the two come together or not at all.
    ...locatorOf(data),
    textSource: knownMember(CitationTextSource, data.textSource),
    ocrConfidence: data.ocrConfidence ?? undefined,
    ocrUncertainNumbers: data.ocrUncertainNumbers ?? undefined,
    // An empty node id is not an identifier, so it is absent rather than empty. It
    // cannot key a citation or resolve a marker click, and treating it as a value
    // makes two such citations indistinguishable.
    nodeId: emptyAsUndefined(Guid.fromJSON(data.nodeId, { allowEmpty: true })),
    matched: data.matched ?? undefined,
    text: data.text ?? undefined,
    withheld: data.withheld ?? undefined,
    retrieverScore: data.retrieverScore ?? undefined,
    semanticScore: data.semanticScore ?? undefined,
  };
}

function locatorOf(data: AssistantCitationJSON): Pick<AssistantCitationParams, "locator" | "locatorKind"> {
  const locatorKind = knownMember(CitationLocatorKind, data.locatorKind);
  return data.locator && locatorKind ? { locator: data.locator, locatorKind } : {};
}

export default class AssistantCitationSchema {
  public static readonly AssistantCitationJSONZ = z.object({
    ordinal: z.number(),
    // The ids are z.guid(), not z.uuid(), for the reason VendorRolloutBoardRowJSONZ gives: a
    // hand-written id such as 1111…1111 would otherwise fail the whole answer.
    fileId: z.guid(),
    fileName: z.string(),
    downloadDocumentId: z.guid().nullish(),
    sourcePath: z.string().nullish(),
    page: z.number().nullish(),
    // What the page has printed on it ("iv", "A-1"), when the document gives one (#6016). Display
    // text: shown in place of `page`, never parsed. Absent from citations recorded before it.
    pageLabel: z.string().nullish(),
    // What kind of thing the source is, by name (#5986), and the section, sheet or slide the text
    // is in and how it was read (#6019). Strings rather than enums here so a name a newer API adds
    // decodes as unknown instead of failing the answer. Absent from citations recorded before them
    // and from a withheld one.
    sourceKind: z.string().nullish(),
    locator: z.string().nullish(),
    locatorKind: z.string().nullish(),
    textSource: z.string().nullish(),
    ocrConfidence: z.number().nullish(),
    ocrUncertainNumbers: z.boolean().nullish(),
    nodeId: z.guid().nullish(),
    matched: z.boolean().nullish(),
    text: z.string().nullish(),
    // Set when the API re-checked this citation on a conversation read back and the reader may
    // no longer see the source, so `text` is empty. Omitted entirely on a live turn, where
    // retrieval has just authorized every citation it returns.
    withheld: z.boolean().nullish(),
    // Retrieval diagnostics. Absent unless the API is running with citation diagnostics
    // enabled: they rank sources against each other and say nothing a user can read as a
    // confidence.
    retrieverScore: z.number().nullish(),
    semanticScore: z.number().nullish(),
  });

  public static readonly AssistantCitationZ = z.object({
    ordinal: z.number(),
    fileId: z.instanceof(Guid),
    fileName: z.string(),
    downloadDocumentId: z.instanceof(Guid).optional(),
    sourcePath: z.string().optional(),
    page: z.number().optional(),
    pageLabel: z.string().optional(),
    sourceKind: z.enum(CitationSourceKind).optional(),
    locator: z.string().optional(),
    locatorKind: z.enum(CitationLocatorKind).optional(),
    textSource: z.enum(CitationTextSource).optional(),
    ocrConfidence: z.number().optional(),
    ocrUncertainNumbers: z.boolean().optional(),
    nodeId: z.instanceof(Guid).optional(),
    matched: z.boolean().optional(),
    text: z.string().optional(),
    withheld: z.boolean().optional(),
    retrieverScore: z.number().optional(),
    semanticScore: z.number().optional(),
  });

  public static validate(values: unknown): AssistantCitationParams {
    return parseOrThrow(AssistantCitationSchema.AssistantCitationZ, values, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): AssistantCitationJSON {
    return parseOrThrow(AssistantCitationSchema.AssistantCitationJSONZ, json, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is AssistantCitationParams {
    try {
      AssistantCitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AssistantCitationJSON {
    try {
      AssistantCitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  /** Validates `json` once and decodes it. Parsing through the codec is the one parse. */
  public static deserialize(json: AssistantCitationJSON): AssistantCitationParams {
    if (isNullOrUndefined(json)) return json;
    return parseOrThrow(AssistantCitationSchema.codec, json, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
  }

  /**
   * Decodes citation JSON that an enclosing schema has already validated, without parsing it again.
   *
   * A response or a conversation validates its citations as part of its own parse. Parsing each one
   * again here, and once more in a constructor, was four zod passes per citation on a live turn and
   * more on a history read (#5992).
   */
  public static decodeParsed(json: AssistantCitationJSON): AssistantCitationParams {
    return decodeCitation(json);
  }

  public static serialize(legalEntity: AssistantCitationParams): AssistantCitationJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AssistantCitationSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AssistantCitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AssistantCitationSchema.AssistantCitationJSONZ,
    AssistantCitationSchema.AssistantCitationZ,
    {
      decode: (data) => decodeCitation(data),
      encode: (citation) => ({
        ordinal: citation.ordinal,
        fileId: citation.fileId.toJSON(),
        fileName: citation.fileName,
        downloadDocumentId: citation.downloadDocumentId?.toJSON(),
        sourcePath: citation.sourcePath,
        page: citation.page,
        pageLabel: citation.pageLabel,
        sourceKind: citation.sourceKind,
        locator: citation.locator,
        locatorKind: citation.locatorKind,
        textSource: citation.textSource,
        ocrConfidence: citation.ocrConfidence,
        ocrUncertainNumbers: citation.ocrUncertainNumbers,
        nodeId: citation.nodeId ? citation.nodeId.toJSON() : undefined,
        matched: citation.matched,
        text: citation.text,
        withheld: citation.withheld,
        retrieverScore: citation.retrieverScore,
        semanticScore: citation.semanticScore,
      }),
    }
  );
}
